import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { defaultPersonAttributes, type IImportColumn } from '@feathr/blackbox';
import {
  Button,
  Fieldset,
  Form,
  FormSummary,
  FormSummaryItem,
  Input,
  Pill,
  Skeleton,
} from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import type { IValidation } from '@feathr/rachis';

import type { IImportButtonProps } from './DataImportPage';
import { isImporterEditable } from './DataImportPage.utils';
import {
  countMappedColumns,
  getRemovedColumns,
  isFirstRowHeader,
} from './DataImportStepThree.utils';

import * as styles from './DataImportPage.css';

interface IProps {
  disabled?: boolean;
  importer: Importer;
  onPrev: () => void;
  ImportButton: React.FunctionComponent<IImportButtonProps>;
  /** False when the page renders step navigation in its own action bar. */
  showActions?: boolean;
}

interface ISummaryProps {
  importer: Importer;
}

interface IColumnSummaryProps {
  importer: Importer;
  /** Labels unmapped columns and lists the columns removed from the mapping. */
  isRevampEnabled: boolean;
}

interface IColumnRowProps {
  columnName: string;
  fieldName: string;
  placeholder?: string;
}

export const validateStepThree = (importer: Importer): IValidation<string[]> => {
  return importer.validate(['name', 'file_name', 'tag_ids', 'column_mappers'], false);
};

const ImportDetailSummary = observer(({ importer }: ISummaryProps) => {
  const { t } = useTranslation();

  return (
    <>
      <FormSummaryItem label={t('Import Name')} value={importer.get('name')} />
      <FormSummaryItem label={t('File Name')} value={importer.get('file_name')} />
    </>
  );
});

const TagsSummary = observer(({ importer }: ISummaryProps) => {
  const { Tags } = useStore();

  if (importer.isOptOut) {
    return null;
  }

  const tags = Tags.list({
    filters: {
      is_archived__ne: true,
      collection: 'Partner',
    },
    pagination: { page_size: 1000 },
  });

  return (
    <FormSummaryItem
      label={'Tags'}
      value={
        !tags.isPending ? (
          tags.models
            .filter((tag) => importer.get('tag_ids', []).includes(tag.id))
            .map((tag) => (
              <Pill key={tag.get('name')} theme={'neutral'}>
                {tag.get('name')}
              </Pill>
            ))
        ) : (
          <Skeleton />
        )
      }
    />
  );
});

const RevampDetailSummary = observer(({ importer }: ISummaryProps) => {
  const { t } = useTranslation();
  const columnNames = importer.get('column_names', []);
  const isHeader = isFirstRowHeader(columnNames);
  const { mapped, total } = countMappedColumns(columnNames, importer.get('column_mappers'));

  let headerValue: string | undefined;
  if (isHeader !== undefined) {
    headerValue = isHeader ? t('Yes') : t('No');
  }

  return (
    <>
      <FormSummaryItem label={t('Import Name')} value={importer.get('name')} />
      <FormSummaryItem label={t('File')} name={'review-file'} value={importer.get('file_name')} />
      <FormSummaryItem
        label={t('First row is a header')}
        name={'review-header-row'}
        value={headerValue}
      />
      <FormSummaryItem
        label={t('Columns mapped')}
        name={'review-columns-mapped'}
        value={t('{{mapped}} of {{total}}', { mapped, total })}
      />
    </>
  );
});

function ColumnRow({ columnName, fieldName, placeholder }: Readonly<IColumnRowProps>): JSX.Element {
  return (
    <Fieldset direction={'column'}>
      <Input disabled={true} type={'text'} value={columnName} />
      <FontAwesomeIcon className={styles.arrow} icon={faArrowRight} />
      <Input disabled={true} placeholder={placeholder} type={'text'} value={fieldName} />
    </Fieldset>
  );
}

const ColumnsSummary = observer(({ importer, isRevampEnabled }: IColumnSummaryProps) => {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const fields = [
    ...defaultPersonAttributes,
    ...importer.getUsedCustomFields(CustomFields).map((f) => f.toJS()),
  ];
  const columns = importer
    .get('column_mappers')
    .filter((column) => column.column_name !== '') as IObservableArray<IImportColumn>;
  const removedColumns = isRevampEnabled
    ? getRemovedColumns(importer.get('column_names', []), importer.get('column_mappers'))
    : [];

  return (
    <FormSummaryItem
      label={isRevampEnabled ? t('Columns') : 'Columns'}
      name={isRevampEnabled ? 'review-columns' : undefined}
      value={
        <>
          {columns.map((column) => (
            <ColumnRow
              columnName={column.column_name}
              fieldName={fields.find((f) => f.id === column.feathr_attr)?.u_key ?? ''}
              key={`${column.column_name}-${column.feathr_attr ?? ''}`}
              placeholder={isRevampEnabled ? t('Not mapped') : undefined}
            />
          ))}
          {removedColumns.map((columnName) => (
            <ColumnRow
              columnName={columnName}
              fieldName={''}
              key={`removed-${columnName}`}
              placeholder={t('Not imported')}
            />
          ))}
        </>
      }
    />
  );
});

function DataImportStepThree({
  disabled = false,
  importer,
  ImportButton,
  onPrev,
  showActions = true,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const isEditable = isImporterEditable(importer.get('state'));

  const actions = showActions
    ? [
        <Button key={'prev'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        <ImportButton
          disabled={disabled || !isEditable}
          importer={importer}
          key={'importButton'}
        />,
      ]
    : undefined;

  const description = importer.isOptOut
    ? t(
        'In order for your data to be properly imported into Feathr, each column of your spreadsheet must be mapped to a field on each person that is being imported. You can map each column to a field that already exists or to a new custom field you create.',
      )
    : t(
        'Review your settings and make sure you are tagging and associating your imported partners how you want.',
      );

  return (
    <Form
      actions={actions}
      description={description}
      label={t('Review')}
      width={isRevampEnabled ? 'wide' : 'narrow'}
    >
      <FormSummary>
        {isRevampEnabled ? (
          <RevampDetailSummary importer={importer} />
        ) : (
          <ImportDetailSummary importer={importer} />
        )}
        <TagsSummary importer={importer} />
        <ColumnsSummary importer={importer} isRevampEnabled={isRevampEnabled} />
      </FormSummary>
    </Form>
  );
}

export default observer(DataImportStepThree);
